import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import ImageUploader, { resolveImageUrl } from "@/components/ImageUploader";
import { Plus, MapPin, Clock, X, Pencil, Trash2, Hotel as HotelIcon, UserCircle2, Compass, Phone, Building2, Car } from "lucide-react";

const EXC_CATEGORIES = ["Ausflug", "Tauchen", "Kultur", "Wellness", "Shopping"];
const LOCATIONS = ["Hurghada", "Marsa Alam", "Sharm el-Sheikh", "Kairo", "Luxor"];
const ROOM_TYPES = ["Doppelzimmer", "Doppelzimmer Meerblick", "Einzelzimmer", "Suite", "Junior Suite", "Familienzimmer", "Familien-Suite", "Bungalow", "Bungalow Beachfront", "Bungalow Gartenblick", "Villa", "Villa mit Pool", "Deluxe Suite Panoramic Pyramids & Sphinx View", "Executive Suite Panoramic Front Pyramids & Sphinx View"];
const BOARD_TYPES = ["All Inclusive", "Halbpension", "Vollpension", "Frühstück", "Ohne"];

const SECTIONS = [
    { id: "excursions", label: "Ausflüge", icon: Compass },
    { id: "hotels", label: "Hotels", icon: HotelIcon },
    { id: "drivers", label: "Fahrer", icon: UserCircle2 },
];

export default function ContentDatabasePage() {
    const [section, setSection] = useState("excursions");

    return (
        <AppLayout>
            <div className="flex items-end justify-between mb-10">
                <div>
                    <p className="pt-eyebrow mb-3">Content-Datenbank</p>
                    <h1 className="pt-h1">Bibliothek der <span className="italic font-normal" style={{ fontFamily: "Fraunces" }}>Bausteine</span></h1>
                    <p className="pt-body mt-3 max-w-2xl">Hinterlege Ausflüge, Hotels und Fahrer einmal — und füge sie per Klick in jede neue Reise ein.</p>
                </div>
            </div>

            <div className="flex flex-wrap gap-1 border-b border-sand-300 mb-8" data-testid="content-tabs">
                {SECTIONS.map((s) => {
                    const Icon = s.icon;
                    return (
                        <button key={s.id} onClick={() => setSection(s.id)} data-testid={`content-tab-${s.id}`}
                            className={`relative px-4 py-3 text-sm font-medium flex items-center gap-2 transition-colors ${
                                section === s.id ? "text-navy" : "text-ink-secondary hover:text-ink"
                            }`}>
                            <Icon className="w-4 h-4" />
                            {s.label}
                            {section === s.id && <span className="absolute -bottom-px left-0 right-0 h-[2px] bg-terracotta" />}
                        </button>
                    );
                })}
            </div>

            <div className="animate-fade-up">
                {section === "excursions" && <ExcursionsSection />}
                {section === "hotels" && <HotelsSection />}
                {section === "drivers" && <DriversSection />}
            </div>
        </AppLayout>
    );
}

// ============ EXCURSIONS ============
function ExcursionsSection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ category: "", location: "" });
    const [editor, setEditor] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter.category) params.category = filter.category;
            if (filter.location) params.location = filter.location;
            const { data } = await api.get("/excursions", { params });
            setItems(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.category, filter.location]);

    const handleSave = async (payload) => {
        try {
            if (payload.id) await api.put(`/excursions/${payload.id}`, payload);
            else await api.post("/excursions", payload);
            setEditor(null); load();
        } catch (e) { alert(formatApiError(e)); }
    };
    const handleDelete = async (id) => {
        if (!window.confirm("Diesen Ausflug löschen?")) return;
        try { await api.delete(`/excursions/${id}`); load(); } catch (e) { alert(formatApiError(e)); }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <div className="flex flex-wrap gap-3" data-testid="excursion-filters">
                    <FilterSelect value={filter.category} onChange={(v) => setFilter({ ...filter, category: v })} placeholder="Alle Kategorien" options={EXC_CATEGORIES} testid="filter-exc-category" />
                    <FilterSelect value={filter.location} onChange={(v) => setFilter({ ...filter, location: v })} placeholder="Alle Standorte" options={LOCATIONS} testid="filter-exc-location" />
                </div>
                <button onClick={() => setEditor("new")} data-testid="new-excursion-button" className="bg-terracotta text-white hover:bg-terracotta-hover rounded-md px-5 py-2.5 font-medium flex items-center gap-2 shadow-card">
                    <Plus className="w-4 h-4" /> Neuer Ausflug
                </button>
            </div>

            {loading ? <Loading /> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="excursions-grid">
                    {items.map((it) => (
                        <ContentCard key={it.id} testid={`excursion-card-${it.id}`}>
                            <CardImage url={it.image_url} category={it.category} isTemplate={it.is_template} />
                            <div className="p-5">
                                <div className="flex items-center gap-3 text-xs text-ink-tertiary mb-2">
                                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{it.location || "—"}</span>
                                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{it.duration || "—"}</span>
                                </div>
                                <h3 className="pt-h3 mb-2 line-clamp-2">{it.title}</h3>
                                <p className="text-sm text-ink-secondary line-clamp-3 mb-4">{it.description}</p>
                                <CardFooter price={it.price} currency={it.currency} priceLabel="p.P." onEdit={() => {
                                    if (it.is_template) {
                                        // Vorlage als Kopie zum Bearbeiten öffnen
                                        const { id, is_template, user_id, created_at, ...rest } = it;
                                        setEditor({ ...rest, title: rest.title });
                                    } else {
                                        setEditor(it);
                                    }
                                }} onDelete={!it.is_template && (() => handleDelete(it.id))} testidPrefix={`excursion-${it.id}`} />
                            </div>
                        </ContentCard>
                    ))}
                </div>
            )}

            {editor && <ExcursionEditor initial={editor === "new" ? null : editor} onClose={() => setEditor(null)} onSave={handleSave} />}
        </>
    );
}

function ExcursionEditor({ initial, onClose, onSave }) {
    const [form, setForm] = useState(initial || {
        title: "", category: "Ausflug", location: "Hurghada", description: "",
        duration: "", price: 0, child_price: 0, currency: "EUR", image_url: "", images: [], includes: [],
    });
    const [includesText, setIncludesText] = useState((initial?.includes || []).join("\n"));

    const submit = (e) => {
        e.preventDefault();
        onSave({
            ...form,
            price: parseFloat(form.price) || 0,
            child_price: parseFloat(form.child_price) || 0,
            includes: includesText.split("\n").map((s) => s.trim()).filter(Boolean),
            images: (form.images || []).filter(Boolean),
        });
    };

    const addExtraImg = () => {
        if ((form.images || []).length >= 4) return; // 1 main + 4 extra = 5 total
        setForm({ ...form, images: [...(form.images || []), ""] });
    };
    const setExtraImg = (i, v) => {
        const next = [...(form.images || [])];
        next[i] = v;
        setForm({ ...form, images: next });
    };
    const removeExtraImg = (i) => {
        setForm({ ...form, images: (form.images || []).filter((_, x) => x !== i) });
    };

    return (
        <ModalShell title={initial ? form.title || "Bearbeiten" : "Neuer Ausflug"} subtitle={initial ? "Bearbeiten" : "Anlegen"} onClose={onClose} testid="excursion-editor">
            <form onSubmit={submit}>
                <div className="space-y-4">
                    <Inp label="Titel" required value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="excursion-title" />
                    <div className="grid grid-cols-2 gap-3">
                        <Sel label="Kategorie" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={EXC_CATEGORIES} testid="excursion-category" />
                        <Sel label="Standort" value={form.location} onChange={(v) => setForm({ ...form, location: v })} options={LOCATIONS} testid="excursion-location" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Inp label="Dauer" value={form.duration} onChange={(v) => setForm({ ...form, duration: v })} placeholder="Halbtag (4h)" testid="excursion-duration" />
                        <Inp type="number" label="Preis Erw." value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="excursion-price" />
                        <Inp type="number" label="Preis Kind <12" value={form.child_price} onChange={(v) => setForm({ ...form, child_price: v })} testid="excursion-child-price" />
                        <Sel label="Währung" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} options={["EUR", "CHF", "USD"]} testid="excursion-currency" />
                    </div>
                    <ImageUploader label="Hauptbild" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="excursion-image" />
                    <div className="bg-sand-100 rounded-md border border-sand-300 p-3">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-secondary">Weitere Bilder ({(form.images || []).length}/4)</p>
                            {(form.images || []).length < 4 && (
                                <button type="button" onClick={addExtraImg} data-testid="excursion-add-image"
                                    className="text-[11px] font-semibold uppercase tracking-[0.12em] text-navy hover:text-terracotta transition-colors">
                                    + Bild hinzufügen
                                </button>
                            )}
                        </div>
                        {(form.images || []).length > 0 ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {(form.images || []).map((url, i) => (
                                    <div key={i} className="relative">
                                        <ImageUploader label="" value={url} onChange={(v) => setExtraImg(i, v)} testid={`excursion-image-extra-${i}`} />
                                        <button type="button" onClick={() => removeExtraImg(i)} data-testid={`excursion-image-extra-${i}-remove`}
                                            className="absolute top-1 right-1 bg-white/90 hover:bg-terracotta hover:text-white text-ink rounded-full w-6 h-6 flex items-center justify-center text-xs shadow-sm transition-colors">×</button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-ink-tertiary text-center py-2">Bis zu 4 weitere Bilder ergänzen den Ausflug.</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Beschreibung</label>
                        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="excursion-description"
                            className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Inklusiv-Leistungen (eine pro Zeile)</label>
                        <textarea value={includesText} onChange={(e) => setIncludesText(e.target.value)} rows={3} data-testid="excursion-includes"
                            className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy" placeholder="Transfer&#10;Mittagessen&#10;Guide" />
                    </div>
                </div>
                <ModalFooter onClose={onClose} submitLabel={initial ? "Speichern" : "Anlegen"} testidSubmit="excursion-save" />
            </form>
        </ModalShell>
    );
}

// ============ HOTELS ============
function HotelsSection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ location: "" });
    const [editor, setEditor] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter.location) params.location = filter.location;
            const { data } = await api.get("/hotels", { params });
            setItems(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.location]);

    const handleSave = async (payload) => {
        try {
            if (payload.id) await api.put(`/hotels/${payload.id}`, payload);
            else await api.post("/hotels", payload);
            setEditor(null); load();
        } catch (e) { alert(formatApiError(e)); }
    };
    const handleDelete = async (id) => {
        if (!window.confirm("Dieses Hotel löschen?")) return;
        try { await api.delete(`/hotels/${id}`); load(); } catch (e) { alert(formatApiError(e)); }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <div className="flex flex-wrap gap-3">
                    <FilterSelect value={filter.location} onChange={(v) => setFilter({ ...filter, location: v })} placeholder="Alle Standorte" options={LOCATIONS} testid="filter-hotel-location" />
                </div>
                <button onClick={() => setEditor("new")} data-testid="new-hotel-button" className="bg-terracotta text-white hover:bg-terracotta-hover rounded-md px-5 py-2.5 font-medium flex items-center gap-2 shadow-card">
                    <Plus className="w-4 h-4" /> Neues Hotel
                </button>
            </div>

            {loading ? <Loading /> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="hotels-grid">
                    {items.map((it) => (
                        <ContentCard key={it.id} testid={`hotel-card-${it.id}`}>
                            <CardImage url={it.image_url} category={it.board_type} isTemplate={it.is_template} />
                            <div className="p-5">
                                <div className="flex items-center gap-3 text-xs text-ink-tertiary mb-2">
                                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{it.location || "—"}</span>
                                    <span className="flex items-center gap-1"><HotelIcon className="w-3 h-3" />{it.room_type || "—"}</span>
                                </div>
                                <h3 className="pt-h3 mb-2 line-clamp-2">{it.name}</h3>
                                <p className="text-sm text-ink-secondary line-clamp-3 mb-4">{it.description}</p>
                                <CardFooter price={it.price_per_night} currency={it.currency} priceLabel="/Nacht" onEdit={() => {
                                    if (it.is_template) {
                                        const { id, is_template, user_id, created_at, ...rest } = it;
                                        setEditor({ ...rest });
                                    } else {
                                        setEditor(it);
                                    }
                                }} onDelete={!it.is_template && (() => handleDelete(it.id))} testidPrefix={`hotel-${it.id}`} />
                            </div>
                        </ContentCard>
                    ))}
                </div>
            )}

            {editor && <HotelEditor initial={editor === "new" ? null : editor} onClose={() => setEditor(null)} onSave={handleSave} />}
        </>
    );
}

function HotelEditor({ initial, onClose, onSave }) {
    const [form, setForm] = useState(initial || {
        name: "", location: "Hurghada", room_type: "Doppelzimmer", board_type: "All Inclusive",
        pricing_mode: "flat",
        price_per_night: 0, cost_per_night: 0,
        child_price_per_night: 0, child_cost_per_night: 0,
        currency: "EUR", description: "", image_url: "", images: [], notes: "",
    });
    const submit = (e) => {
        e.preventDefault();
        onSave({
            ...form,
            price_per_night: parseFloat(form.price_per_night) || 0,
            cost_per_night: parseFloat(form.cost_per_night) || 0,
            child_price_per_night: parseFloat(form.child_price_per_night) || 0,
            child_cost_per_night: parseFloat(form.child_cost_per_night) || 0,
            images: (form.images || []).filter(Boolean),
        });
    };

    const fill50 = () => setForm({
        ...form,
        child_price_per_night: Math.round((Number(form.price_per_night || 0) * 0.5) * 100) / 100,
        child_cost_per_night: Math.round((Number(form.cost_per_night || 0) * 0.5) * 100) / 100,
    });
    const isPerPax = (form.pricing_mode || "flat") === "per_pax";

    const addExtraImg = () => {
        if ((form.images || []).length >= 4) return;
        setForm({ ...form, images: [...(form.images || []), ""] });
    };
    const setExtraImg = (i, v) => {
        const next = [...(form.images || [])];
        next[i] = v;
        setForm({ ...form, images: next });
    };
    const removeExtraImg = (i) => {
        setForm({ ...form, images: (form.images || []).filter((_, x) => x !== i) });
    };

    return (
        <ModalShell title={initial ? form.name || "Hotel bearbeiten" : "Neues Hotel"} subtitle={initial ? "Bearbeiten" : "Anlegen"} onClose={onClose} testid="hotel-editor">
            <form onSubmit={submit}>
                <div className="space-y-4">
                    <Inp label="Hotelname" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="hotel-name" />
                    <div className="grid grid-cols-2 gap-3">
                        <Sel label="Standort" value={form.location} onChange={(v) => setForm({ ...form, location: v })} options={LOCATIONS} testid="hotel-location" />
                        <Sel label="Zimmer" value={form.room_type} onChange={(v) => setForm({ ...form, room_type: v })} options={ROOM_TYPES} testid="hotel-room" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <Sel label="Verpflegung" value={form.board_type} onChange={(v) => setForm({ ...form, board_type: v })} options={BOARD_TYPES} testid="hotel-board" />
                        <Sel label="Preis-Modus" value={form.pricing_mode || "flat"} onChange={(v) => setForm({ ...form, pricing_mode: v })}
                            options={[{ value: "flat", label: "Pauschal / Nacht" }, { value: "per_pax", label: "Pro Person / Nacht" }]} testid="hotel-pricing-mode" />
                        <Sel label="Währung" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} options={["EUR", "CHF", "USD"]} testid="hotel-currency" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Inp type="number" label={isPerPax ? "Verkauf pro Erwachsenem / Nacht" : "Preis / Nacht (Pauschal)"} value={form.price_per_night} onChange={(v) => setForm({ ...form, price_per_night: v })} testid="hotel-price" />
                        <Inp type="number" label={isPerPax ? "Einkauf pro Erwachsenem / Nacht (intern)" : "Einkauf / Nacht (intern, Pauschal)"} value={form.cost_per_night} onChange={(v) => setForm({ ...form, cost_per_night: v })} testid="hotel-cost-price" />
                    </div>
                    <div className="bg-sand-100/70 border border-dashed border-sand-300 rounded-md px-3 py-3">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-secondary">
                                Kinderpreise · <span className="normal-case font-normal text-ink-tertiary">
                                    {isPerPax ? "Pro Kind <12 / Nacht (leer = Erw.-Preis)" : "Aufschlag pro Kind <12 / Nacht"}
                                </span>
                            </p>
                            <button type="button" onClick={fill50} data-testid="hotel-fill-50"
                                className="text-[10px] uppercase tracking-[0.14em] text-navy hover:text-navy-hover border border-navy/30 hover:border-navy rounded px-2 py-1 transition-colors">
                                50 % vorschlagen
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Inp type="number" label={isPerPax ? "Verkauf pro Kind / Nacht" : "Verkauf Aufschlag Kind / Nacht"} value={form.child_price_per_night} onChange={(v) => setForm({ ...form, child_price_per_night: v })} testid="hotel-child-price" />
                            <Inp type="number" label={isPerPax ? "Einkauf pro Kind / Nacht (intern)" : "Einkauf Aufschlag Kind / Nacht (intern)"} value={form.child_cost_per_night} onChange={(v) => setForm({ ...form, child_cost_per_night: v })} testid="hotel-child-cost-price" />
                        </div>
                    </div>
                    <ImageUploader label="Hauptbild" value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="hotel-image" />
                    <div className="bg-sand-100 rounded-md border border-sand-300 p-3">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-secondary">Weitere Bilder ({(form.images || []).length}/4)</p>
                            {(form.images || []).length < 4 && (
                                <button type="button" onClick={addExtraImg} data-testid="hotel-add-image"
                                    className="text-[11px] font-semibold uppercase tracking-[0.12em] text-navy hover:text-terracotta transition-colors">
                                    + Bild hinzufügen
                                </button>
                            )}
                        </div>
                        {(form.images || []).length > 0 ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {(form.images || []).map((url, i) => (
                                    <div key={i} className="relative">
                                        <ImageUploader label="" value={url} onChange={(v) => setExtraImg(i, v)} testid={`hotel-image-extra-${i}`} />
                                        <button type="button" onClick={() => removeExtraImg(i)} data-testid={`hotel-image-extra-${i}-remove`}
                                            className="absolute top-1 right-1 bg-white/90 hover:bg-terracotta hover:text-white text-ink rounded-full w-6 h-6 flex items-center justify-center text-xs shadow-sm transition-colors">×</button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-ink-tertiary text-center py-2">Bis zu 4 weitere Bilder ergänzen das Hotel.</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">Beschreibung</label>
                        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="hotel-description"
                            className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy" />
                    </div>
                    <Inp label="Notizen" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="hotel-notes" />
                </div>
                <ModalFooter onClose={onClose} submitLabel={initial ? "Speichern" : "Anlegen"} testidSubmit="hotel-save" />
            </form>
        </ModalShell>
    );
}

// ============ DRIVERS ============
function DriversSection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ location: "" });
    const [editor, setEditor] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter.location) params.location = filter.location;
            const { data } = await api.get("/drivers", { params });
            setItems(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.location]);

    const handleSave = async (payload) => {
        try {
            if (payload.id) await api.put(`/drivers/${payload.id}`, payload);
            else await api.post("/drivers", payload);
            setEditor(null); load();
        } catch (e) { alert(formatApiError(e)); }
    };
    const handleDelete = async (id) => {
        if (!window.confirm("Diesen Fahrer löschen?")) return;
        try { await api.delete(`/drivers/${id}`); load(); } catch (e) { alert(formatApiError(e)); }
    };

    return (
        <>
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <div className="flex flex-wrap gap-3">
                    <FilterSelect value={filter.location} onChange={(v) => setFilter({ ...filter, location: v })} placeholder="Alle Standorte" options={LOCATIONS} testid="filter-driver-location" />
                </div>
                <button onClick={() => setEditor("new")} data-testid="new-driver-button" className="bg-terracotta text-white hover:bg-terracotta-hover rounded-md px-5 py-2.5 font-medium flex items-center gap-2 shadow-card">
                    <Plus className="w-4 h-4" /> Neuer Fahrer
                </button>
            </div>

            {loading ? <Loading /> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="drivers-grid">
                    {items.map((it) => (
                        <ContentCard key={it.id} testid={`driver-card-${it.id}`}>
                            <div className="p-5 border-b border-sand-300 bg-sand-100 flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-navy flex items-center justify-center text-sand flex-shrink-0">
                                    <UserCircle2 className="w-7 h-7" strokeWidth={1.5} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h3 className="font-bold text-ink truncate" style={{ fontFamily: "Space Grotesk" }}>{it.name}</h3>
                                    <p className="text-xs text-ink-secondary truncate flex items-center gap-1.5"><Building2 className="w-3 h-3" />{it.company_name || "—"}</p>
                                </div>
                                {it.is_template && <span className="text-[9px] font-bold uppercase tracking-[0.14em] px-2 py-0.5 rounded bg-navy text-sand flex-shrink-0">Template</span>}
                            </div>
                            <div className="p-5 space-y-2 text-sm">
                                <p className="flex items-center gap-2 text-ink"><Phone className="w-3.5 h-3.5 text-navy" /> <span className="font-mono">{it.mobile_no || "—"}</span></p>
                                <p className="flex items-center gap-2 text-ink-secondary"><Car className="w-3.5 h-3.5 text-navy" /> {it.vehicle || "—"}</p>
                                <p className="flex items-center gap-2 text-ink-secondary"><MapPin className="w-3.5 h-3.5 text-navy" /> {it.location || "—"}</p>
                                {it.notes && <p className="text-xs text-ink-tertiary line-clamp-2 pt-2 italic">{it.notes}</p>}
                            </div>
                            <CardFooter price={it.price} currency={it.currency} priceLabel="/Transfer" onEdit={() => {
                                if (it.is_template) {
                                    const { id, is_template, user_id, created_at, ...rest } = it;
                                    setEditor({ ...rest });
                                } else {
                                    setEditor(it);
                                }
                            }} onDelete={!it.is_template && (() => handleDelete(it.id))} testidPrefix={`driver-${it.id}`} />
                        </ContentCard>
                    ))}
                </div>
            )}

            {editor && <DriverEditor initial={editor === "new" ? null : editor} onClose={() => setEditor(null)} onSave={handleSave} />}
        </>
    );
}

function DriverEditor({ initial, onClose, onSave }) {
    const [form, setForm] = useState(initial || {
        name: "", company_name: "", mobile_no: "", vehicle: "Privat-Van",
        location: "Hurghada", price: 0, currency: "EUR", notes: "",
    });
    const submit = (e) => { e.preventDefault(); onSave({ ...form, price: parseFloat(form.price) || 0 }); };

    return (
        <ModalShell title={initial ? form.name || "Fahrer bearbeiten" : "Neuer Fahrer"} subtitle={initial ? "Bearbeiten" : "Anlegen"} onClose={onClose} testid="driver-editor">
            <form onSubmit={submit}>
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <Inp label="Fahrer Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="driver-name" />
                        <Inp label="Firma" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testid="driver-company" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Inp type="tel" label="Mobile Nr." value={form.mobile_no} onChange={(v) => setForm({ ...form, mobile_no: v })} placeholder="+20 100 …" testid="driver-mobile" />
                        <Inp label="Fahrzeug" value={form.vehicle} onChange={(v) => setForm({ ...form, vehicle: v })} placeholder="Mercedes V-Klasse" testid="driver-vehicle" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <Sel label="Standort" value={form.location} onChange={(v) => setForm({ ...form, location: v })} options={LOCATIONS} testid="driver-location" />
                        <Inp type="number" label="Preis" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="driver-price" />
                        <Sel label="Währung" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} options={["EUR", "CHF", "USD"]} testid="driver-currency" />
                    </div>
                    <Inp label="Notizen" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testid="driver-notes" />
                </div>
                <ModalFooter onClose={onClose} submitLabel={initial ? "Speichern" : "Anlegen"} testidSubmit="driver-save" />
            </form>
        </ModalShell>
    );
}

// ============ SHARED HELPERS ============
function ContentCard({ children, testid }) {
    return <div data-testid={testid} className="pt-card group">{children}</div>;
}

function CardImage({ url, category, isTemplate }) {
    return (
        <div className="aspect-[4/3] bg-sand-deep relative overflow-hidden">
            {url && <img src={resolveImageUrl(url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
            {category && <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded bg-white/95 backdrop-blur text-navy">{category}</span>}
            {isTemplate && <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-1 rounded bg-navy/90 text-sand">Template</span>}
        </div>
    );
}

function CardFooter({ price, currency, priceLabel, onEdit, onDelete, testidPrefix }) {
    return (
        <div className="flex items-center justify-between px-5 py-4 border-t border-sand-300">
            <div>
                <p className="text-lg font-bold text-terracotta" style={{ fontFamily: "Space Grotesk" }}>
                    {currency === "EUR" ? "€" : currency} {Number(price || 0).toFixed(0)}
                </p>
                <p className="text-[10px] text-ink-tertiary uppercase tracking-[0.14em]">{priceLabel}</p>
            </div>
            <div className="flex gap-1">
                {onEdit && (
                    <button onClick={onEdit} data-testid={`${testidPrefix}-edit`} className="p-2 rounded hover:bg-sand-deep text-ink-secondary hover:text-navy transition-colors">
                        <Pencil className="w-4 h-4" />
                    </button>
                )}
                {onDelete && (
                    <button onClick={onDelete} data-testid={`${testidPrefix}-delete`} className="p-2 rounded hover:bg-terracotta/10 text-ink-secondary hover:text-terracotta transition-colors">
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}

function ModalShell({ title, subtitle, onClose, children, testid }) {
    return (
        <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-hover w-full max-w-2xl max-h-[90vh] overflow-auto relative" onClick={(e) => e.stopPropagation()} data-testid={testid}>
                <span className="pt-accent-bar" />
                <button type="button" onClick={onClose} className="absolute top-4 right-4 p-2 rounded hover:bg-sand-deep z-10" data-testid={`${testid}-close`}><X className="w-4 h-4" /></button>
                <div className="p-8">
                    <p className="pt-eyebrow mb-2">{subtitle}</p>
                    <h2 className="pt-h2 mb-6 truncate pr-10">{title}</h2>
                    {children}
                </div>
            </div>
        </div>
    );
}

function ModalFooter({ onClose, submitLabel, testidSubmit }) {
    return (
        <div className="flex justify-end gap-3 mt-7">
            <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-md border border-sand-300 text-ink-secondary hover:bg-sand-deep">Abbrechen</button>
            <button type="submit" data-testid={testidSubmit} className="px-5 py-2.5 rounded-md bg-navy text-sand hover:bg-navy-hover font-medium">{submitLabel}</button>
        </div>
    );
}

function Loading() {
    return <div className="bg-white border border-sand-300 rounded-lg p-12 text-center text-ink-secondary">Lade Inhalte…</div>;
}

function FilterSelect({ value, onChange, placeholder, options, testid }) {
    return (
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="bg-white border border-sand-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy">
            <option value="">{placeholder}</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
    );
}

function Inp({ label, value, onChange, type = "text", required = false, placeholder = "", testid }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">{label}</label>
            <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
                className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy" />
        </div>
    );
}
function Sel({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-ink-secondary mb-2">{label}</label>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="w-full bg-white border border-sand-300 rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-navy">
                {options.map((o) => {
                    const val = typeof o === "object" ? o.value : o;
                    const lbl = typeof o === "object" ? o.label : o;
                    return <option key={val} value={val}>{lbl}</option>;
                })}
            </select>
        </div>
    );
}
